import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify'; // Importa toast para mostrar notificaciones
import { getUserById, updateUser } from 'services/users'; // Asegúrate de tener estas funciones en tu servicio
import { getAll } from 'services/roles'; // Importa la función para obtener los roles

const EditUser = () => {
  const { id } = useParams(); // Obtiene el id de los parámetros de la URL
  const navigate = useNavigate(); // Para redirigir después de guardar
  const [userId] = useState(id || localStorage.getItem('selectedUserId')); // Obtiene el id desde localStorage si no está en los parámetros
  const [user, setUser] = useState(null); // Estado para almacenar los datos del usuario
  const [roles, setRoles] = useState([]); // Estado para almacenar los roles
  const [loading, setLoading] = useState(true); // Estado para manejar la carga
  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    role_id: '', // Cambiado a role_id para manejar el ID del rol
    is_active: false
  }); // Estado para manejar el formulario
  const [errors, setErrors] = useState({}); // Estado para manejar errores de validación

  useEffect(() => {
    if (userId) {
      // Llama a la API para obtener los datos del usuario
      getUserById(userId)
        .then(response => {
          if (response.result) {
            const userData = response.user; // Extrae los datos del usuario
            setUser(userData); // Almacena los datos del usuario
            setFormData({
              user_name: userData.user_name,
              email: userData.email,
              role_id: userData.role_id, // Usa role_id en lugar de role
              is_active: userData.is_active
            }); // Inicializa el formulario con los datos del usuario
          } else {
            console.error('No se encontró el usuario.');
          }
          setLoading(false); // Finaliza la carga
        })
        .catch(error => {
          console.error('Error al obtener los datos del usuario:', error);
          setLoading(false); // Finaliza la carga incluso si hay un error
        });
    } else {
      console.error('No se encontró un id de usuario.');
      setLoading(false);
    }

    // Llama a la API para obtener los roles
    getAll()
      .then(response => {
        setRoles(response); // Almacena los roles en el estado
      })
      .catch(error => {
        console.error('Error al obtener los roles:', error);
      });
  }, [userId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setErrors({
      ...errors,
      [name]: '' // Limpia el error al cambiar el valor
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.user_name.trim())
      newErrors.user_name = 'El nombre es requerido.';
    if (!formData.email.trim())
      newErrors.email = 'El correo electrónico es requerido.';
    if (!formData.role_id) newErrors.role_id = 'El rol es requerido.';
    return newErrors;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Por favor, completa todos los campos requeridos.', {
        theme: 'colored'
      });
      return;
    }

    // Llama a la API para actualizar los datos del usuario
    updateUser(userId, formData)
      .then(() => {
        toast.success(
          `Usuario ${formData.user_name} actualizado exitosamente`,
          {
            theme: 'colored'
          }
        ); // Muestra un mensaje de éxito
        navigate('/users'); // Redirige a la lista de usuarios
      })
      .catch(error => {
        console.error('Error al actualizar el usuario:', error);
        toast.error('Ocurrió un error al actualizar el usuario', {
          theme: 'colored'
        }); // Muestra un mensaje de error
      });
  };

  if (loading) {
    return (
      <div className="text-center py-3">Cargando datos del usuario...</div>
    );
  }

  if (!user) {
    return <div className="text-center py-3">No se encontró el usuario.</div>;
  }

  return (
    <Card>
      <Card.Header>
        <h5>Editar Usuario</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              name="user_name"
              value={formData.user_name}
              onChange={handleChange}
              placeholder="Ingresa el nombre del usuario"
              isInvalid={!!errors.user_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_name}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Correo Electrónico</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Ingresa el correo electrónico"
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Rol</Form.Label>
            <Form.Select
              name="role_id"
              value={formData.role_id}
              onChange={handleChange}
              isInvalid={!!errors.role_id}
            >
              <option value="">Selecciona un rol</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.role_id}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <div
              onClick={() =>
                setFormData(prev => ({
                  ...prev,
                  is_active: !prev.is_active // Alterna el estado de is_active
                }))
              }
              style={{ cursor: 'pointer' }} // Cambia el cursor para indicar que es interactivo
            >
              <Form.Check
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange} // Sigue manejando cambios desde el checkbox
                label={formData.is_active ? 'Activo' : 'Inactivo'} // Cambia la etiqueta dinámicamente
              />
            </div>
          </Form.Group>
          <Button variant="primary" type="submit">
            Guardar Cambios
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default EditUser;
